import { AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import logoIcon from "@/assets/cyphron_logo/logo-icon.svg";

interface ErrorFallbackProps {
  error: Error;
  eventId: string;
  resetError: () => void;
}

/**
 * Full-viewport Cyphron-branded error surface rendered by `ErrorBoundary`
 * when a React error escapes the tree.
 *
 * Strings are verbatim from `.planning/phases/18-observability-hardening/18-UI-SPEC.md`
 * §Flow C (ErrorBoundary) and §Copywriting Contract — do not paraphrase without
 * updating the UI-SPEC first.
 *
 * Security: `error.stack` is intentionally NOT rendered (UI-SPEC non-goals).
 * The stack lives only in Sentry; the user sees `eventId` for support handoff.
 */
export function ErrorFallback({ eventId, resetError }: ErrorFallbackProps) {
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(eventId);
      toast.success("ID copiado");
    } catch {
      toast.error("Seu navegador não permitiu copiar.");
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
      {/* Background */}
      <div className="absolute inset-0 bg-[#080C14]" />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 0%, rgba(0,102,255,0.08) 0%, transparent 60%)",
        }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[420px]">
        <div
          className="relative rounded-2xl p-8 pt-10"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {/* Logo + Icon + Heading */}
          <div className="flex flex-col items-center mb-6">
            <img src={logoIcon} alt="Cyphron" className="h-10 w-10 mb-4 opacity-60" />
            <AlertTriangle
              size={48}
              className="text-amber-500 mb-4"
              aria-hidden="true"
            />
            <h1
              className="text-[28px] font-semibold text-[#F8FAFC] text-center"
              style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}
            >
              Algo não saiu como esperado
            </h1>
          </div>

          {/* Body */}
          <p
            className="text-base text-[#94A3B8] text-center mb-6"
            style={{ lineHeight: 1.5 }}
          >
            Já recebemos o relatório do erro e vamos investigar. Você pode tentar de novo ou voltar ao painel.
          </p>

          {/* Event ID + Copy */}
          <div
            className="rounded-lg p-3 mb-6"
            style={{
              background: "rgba(11,18,33,0.60)",
              border: "1px solid rgba(140,160,220,0.07)",
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-[#475569]">
                ID do erro:{" "}
                <code className="text-[#94A3B8] font-mono">{eventId || "—"}</code>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                aria-label="Copiar ID"
                title="Copiar ID"
                className="h-8"
                disabled={!eventId}
              >
                <Copy size={14} />
              </Button>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={resetError}
              className="w-full h-10 text-sm font-semibold rounded-lg bg-[#84FF00] text-white hover:bg-[#84FF00]/90 shadow-[0_0_20px_-4px_rgba(132,255,0,0.4)] hover:shadow-[0_0_28px_-4px_rgba(132,255,0,0.55)]"
            >
              Tentar novamente
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = "/";
              }}
              className="w-full h-10 text-sm rounded-lg"
            >
              Voltar ao início
            </Button>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-[#475569] mt-6">
            <a
              href={`mailto:suporte@cyphronsec.com?subject=${encodeURIComponent(
                `Erro ${eventId}`,
              )}`}
              className="hover:underline"
            >
              Enviar detalhes adicionais
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
